import React, { useEffect, useState } from 'react';
import { FaCheckCircle, FaExclamationCircle, FaInfoCircle, FaTimes, FaExclamationTriangle, FaRocket } from 'react-icons/fa';

const Toast = ({ message, type = 'info', onClose, duration = 5000, action = null }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [duration]);

  const handleClose = () => {
    setIsLeaving(true);
    setTimeout(() => {
      setIsVisible(false);
      if (onClose) onClose();
    }, 300);
  };

  if (!isVisible) return null;

  const getTypeStyles = () => {
    switch (type) {
      case 'success':
        return {
          bg: 'bg-gradient-to-r from-secondary-50 to-secondary-100',
          border: 'border-secondary-400',
          icon: FaCheckCircle,
          iconColor: 'text-secondary-500',
          textColor: 'text-secondary-800'
        };
      case 'error':
        return {
          bg: 'bg-gradient-to-r from-red-50 to-red-100',
          border: 'border-red-400',
          icon: FaExclamationCircle,
          iconColor: 'text-red-500',
          textColor: 'text-red-800'
        };
      case 'warning':
        return {
          bg: 'bg-gradient-to-r from-accent-50 to-amber-100',
          border: 'border-accent-400',
          icon: FaExclamationTriangle,
          iconColor: 'text-accent-500',
          textColor: 'text-amber-800'
        };
      case 'upgrade':
        return {
          bg: 'bg-gradient-to-r from-primary-50 to-secondary-50',
          border: 'border-primary-400',
          icon: FaRocket,
          iconColor: 'text-primary-500',
          textColor: 'text-neutral-800'
        };
      default:
        return {
          bg: 'bg-gradient-to-r from-neutral-50 to-neutral-100',
          border: 'border-neutral-400',
          icon: FaInfoCircle,
          iconColor: 'text-neutral-500',
          textColor: 'text-neutral-800'
        };
    }
  };

  const styles = getTypeStyles();
  const Icon = styles.icon;

  return (
    <div
      className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-50 max-w-lg w-full mx-4 transition-all duration-300 ${
        isLeaving ? 'opacity-0 -translate-y-2' : 'opacity-100 translate-y-0'
      }`}
    >
      <div
        className={`${styles.bg} border-l-4 ${styles.border} rounded-lg shadow-elevated p-4 flex items-start gap-3`}
      >
        <Icon className={`${styles.iconColor} text-xl flex-shrink-0 mt-0.5`} />
        <div className="flex-1 min-w-0">
          <p className={`${styles.textColor} font-medium text-sm leading-relaxed`}>
            {message}
          </p>
          {action && (
            <button
              onClick={action.onClick}
              className="mt-2 inline-flex items-center gap-1 px-4 py-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white text-sm font-semibold rounded-lg hover:from-primary-600 hover:to-primary-700 transition-all duration-200 shadow-sm"
            >
              <FaRocket className="text-xs" />
              {action.label}
            </button>
          )}
        </div>
        <button
          onClick={handleClose}
          className="text-neutral-400 hover:text-neutral-600 transition-colors p-1"
        >
          <FaTimes className="text-sm" />
        </button>
      </div>
    </div>
  );
};

// Toast Container to manage multiple toasts
export const ToastContainer = ({ toasts, removeToast }) => {
  return (
    <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-lg px-4 space-y-2">
      {toasts.map((toast, index) => (
        <div
          key={toast.id}
          style={{ transform: `translateY(${index * 8}px)` }}
          className="transition-all duration-300"
        >
          <Toast
            message={toast.message}
            type={toast.type}
            action={toast.action}
            duration={toast.duration}
            onClose={() => removeToast(toast.id)}
          />
        </div>
      ))}
    </div>
  );
};

// Hook for managing toasts
export const useToast = () => {
  const [toasts, setToasts] = useState([]);

  const addToast = (message, type = 'info', options = {}) => {
    const id = Date.now() + Math.random();
    const toast = {
      id,
      message,
      type,
      duration: options.duration || 5000,
      action: options.action || null
    };
    setToasts(prev => [...prev, toast]);
    return id;
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  const showSuccess = (message, options) => addToast(message, 'success', options);
  const showError = (message, options) => addToast(message, 'error', options);
  const showWarning = (message, options) => addToast(message, 'warning', options);
  const showInfo = (message, options) => addToast(message, 'info', options);
  const showUpgrade = (message, options) => addToast(message, 'upgrade', { duration: 8000, ...options });

  return {
    toasts,
    addToast,
    removeToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showUpgrade
  };
};

export default Toast;
